// import { fetchLogoutTimeout } from '@/api'
import type { AuthenticatedUserData, RiskReviewRequiredData } from '@/api/modules/auth'
import apiAuth from '@/api/modules/auth'
import apiUser from '@/api/modules/user'
import router from '@/router'
import { getAliyunDeviceToken } from '@/utils/aliyunDeviceRisk'
import dayjs from '@/utils/dayjs'
import { syncEchoAuthToken } from '@/utils/echo'
import { toast } from 'vue-sonner'

interface RoleInfo {
  is_super_admin: boolean
  is_group_leader: boolean
  is_department_manager: boolean
  is_operation: boolean
  is_supplier: boolean
  is_standalone_station_assistant: boolean
  is_finance: boolean
}

const RISK_REVIEW_STORAGE_KEY = 'loginRiskReview'

interface PendingLoginRiskReview {
  reviewToken: string
  expiresAt: string
  expiresAtTimestamp: number
  message: string
  account: string
}

type LoginFailureReason
  = 'request-failed'
    | 'risk-review-invalid'
    | 'risk-review-expired'
    | 'session-invalid'

type LoginResult
  = { status: 'authenticated' }
    | { status: 'risk-review-required' }
    | { status: 'failed', reason: LoginFailureReason, message?: string }

type RiskReviewCompletionResult
  = { status: 'authenticated' }
    | { status: 'expired' }
    | { status: 'failed', message?: string }

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function getErrorMessage(error: unknown): string | undefined {
  if (error instanceof Error) {
    return error.message
  }
  if (!isRecord(error)) {
    return undefined
  }

  const message = typeof error.message === 'string' ? error.message : undefined
  const responseError = typeof error.error === 'string' ? error.error : undefined
  return message || responseError
}

function parseRiskReviewExpiry(expiresAt: string): number | null {
  // Apifox 当前返回无时区时间，按浏览器本地时区（项目默认 Asia/Shanghai）解析。
  const expiresAtTimestamp = dayjs(expiresAt).valueOf()
  return Number.isFinite(expiresAtTimestamp) ? expiresAtTimestamp : null
}

function readPendingRiskReview(): PendingLoginRiskReview | null {
  const storedValue = sessionStorage.getItem(RISK_REVIEW_STORAGE_KEY)
  if (!storedValue) {
    return null
  }

  try {
    const parsed: unknown = JSON.parse(storedValue)
    const expiresAtTimestamp = isRecord(parsed) && typeof parsed.expiresAt === 'string'
      ? parseRiskReviewExpiry(parsed.expiresAt)
      : null
    if (
      !isRecord(parsed)
      || typeof parsed.reviewToken !== 'string'
      || typeof parsed.expiresAt !== 'string'
      || expiresAtTimestamp === null
      || typeof parsed.message !== 'string'
      || typeof parsed.account !== 'string'
    ) {
      sessionStorage.removeItem(RISK_REVIEW_STORAGE_KEY)
      return null
    }

    return {
      reviewToken: parsed.reviewToken,
      expiresAt: parsed.expiresAt,
      expiresAtTimestamp,
      message: parsed.message,
      account: parsed.account,
    }
  }
  catch {
    sessionStorage.removeItem(RISK_REVIEW_STORAGE_KEY)
    return null
  }
}

export const useUserStore = defineStore(
  'user',
  () => {
    const settingsStore = useSettingsStore()
    const routeStore = useRouteStore()
    const menuStore = useMenuStore()
    // const tabbarStore = useTabbarStore()

    const account = ref(localStorage.account ?? '')
    const token = ref(localStorage.token ?? '')
    const avatar = ref(localStorage.avatar ?? '')
    const user_identifier = ref(localStorage.user_identifier ?? '')
    const email = ref(localStorage.email ?? '')
    const permissions = ref<string[]>([])
    const isLogin = computed(() => !!token.value)
    const isAdmin = ref(JSON.parse(localStorage.getItem('isAdmin') || 'false'))
    const isLicensee = ref(false)
    const roleInfo = ref<RoleInfo | null>(null)
    const user_id = ref(localStorage.user_id ?? '')
    const tour = ref(localStorage.getItem('tour') === 'true')
    const pendingRiskReview = ref<PendingLoginRiskReview | null>(readPendingRiskReview())

    let tokenRefreshTimer: NodeJS.Timeout | null = null
    let riskReviewCompletionPromise: Promise<RiskReviewCompletionResult> | null = null

    // 角色判断 getters
    const isSuperAdmin = computed(() => !!roleInfo.value?.is_super_admin)
    const isGroupLeader = computed(() => !!roleInfo.value?.is_group_leader)
    const isDepartmentManager = computed(() => !!roleInfo.value?.is_department_manager)
    const isOperation = computed(() => !!roleInfo.value?.is_operation)
    const isSupplier = computed(() => !!roleInfo.value?.is_supplier)
    const isStandaloneStationAssistant = computed(() => !!roleInfo.value?.is_standalone_station_assistant)
    const isFinance = computed(() => !!roleInfo.value?.is_finance)

    // function startTokenRefreshTimer() {
    //   if (tokenRefreshTimer) {
    //     clearInterval(tokenRefreshTimer)
    //   }

    //   tokenRefreshTimer = setInterval(() => {
    //     const expireTime = Number(localStorage.getItem('tokenExpireTime') ?? 0)
    //     const remaining = expireTime - Date.now()

    //     if (remaining <= 180000) {
    //       refreshToken()
    //     }
    //   }, 60000)
    // }

    async function refreshToken() {
      try {
        const res = await apiAuth.refresh()
        if (res.status !== 1) {
          throw new Error('Token刷新失败')
        }
        token.value = res.data.token
        localStorage.setItem('token', res.data.token)
        syncEchoAuthToken(res.data.token)
        window.dispatchEvent(new CustomEvent('echo:token-refreshed', {
          detail: {
            token: res.data.token,
          },
        }))
        const expiresIn = res.data.expires_in || 3600
        const newExpireTime = Date.now() + expiresIn * 1000
        localStorage.setItem('tokenExpireTime', newExpireTime.toString())
        // startTokenRefreshTimer()
      }
      catch (error) {
        console.error('Token刷新失败，即将登出', error)
        toast.error('登录状态已过期，请重新登录')
        throw error
      }
    }

    function clearPendingRiskReview() {
      pendingRiskReview.value = null
      sessionStorage.removeItem(RISK_REVIEW_STORAGE_KEY)
    }

    function setPendingRiskReview(data: RiskReviewRequiredData, message: string, loginAccount: string): boolean {
      const expiresAtTimestamp = parseRiskReviewExpiry(data.expires_at)
      if (!data.review_token || expiresAtTimestamp === null) {
        return false
      }

      pendingRiskReview.value = {
        reviewToken: data.review_token,
        expiresAt: data.expires_at,
        expiresAtTimestamp,
        message,
        account: loginAccount,
      }
      sessionStorage.setItem(RISK_REVIEW_STORAGE_KEY, JSON.stringify(pendingRiskReview.value))
      return true
    }

    function hasValidSessionData(data: AuthenticatedUserData): boolean {
      return typeof data.token === 'string'
        && data.token.length > 0
        && typeof data.email === 'string'
    }

    function applyLoginSession(loginAccount: string, data: AuthenticatedUserData) {
      const normalizedUserId = data.user_id === undefined ? '' : String(data.user_id)
      const normalizedAvatar = data.avatar || ''
      const normalizedUserIdentifier = data.user_identifier || ''

      // 存储用户信息，但主要依赖HttpOnly Cookie进行身份验证
      localStorage.setItem('account', loginAccount)
      localStorage.setItem('user_id', normalizedUserId)
      localStorage.setItem('token', data.token) // 保留token以兼容旧API
      localStorage.setItem('avatar', normalizedAvatar)
      localStorage.setItem('user_identifier', normalizedUserIdentifier)
      localStorage.setItem('email', data.email)
      localStorage.setItem('loginTime', Date.now().toString())

      const tourFlag = data.is_first_login === 1
      if (tourFlag) {
        localStorage.setItem('tour', 'true')
      }
      else {
        localStorage.removeItem('tour')
      }

      // 存储过期时间和刷新时间（expires_in 为单位：秒）
      const expiresIn = data.expires_in || 1800 // 默认 30 分钟有效
      const expireTime = Date.now() + expiresIn * 1000
      localStorage.setItem('tokenExpireTime', expireTime.toString())

      // 更新状态
      account.value = loginAccount
      user_id.value = normalizedUserId
      token.value = data.token
      avatar.value = normalizedAvatar
      user_identifier.value = normalizedUserIdentifier
      email.value = data.email
      tour.value = tourFlag
      syncEchoAuthToken(data.token)

      // fetchLogoutTimeout()
      // startTokenRefreshTimer()
      checkAdmin()
    }

    async function login(data: {
      account: string
      password: string
    }): Promise<LoginResult> {
      clearPendingRiskReview()
      try {
        const deviceToken = await getAliyunDeviceToken()
        const res = await apiAuth.login({
          username: data.account,
          password: data.password,
          device_token: deviceToken,
        })

        if (res.status !== 1) {
          return {
            status: 'failed',
            reason: 'request-failed',
            message: res.error || res.message,
          }
        }

        if (res.data.requires_risk_review === true) {
          if (!setPendingRiskReview(res.data, res.message, data.account)) {
            return {
              status: 'failed',
              reason: 'risk-review-invalid',
            }
          }
          if ((pendingRiskReview.value?.expiresAtTimestamp ?? 0) <= Date.now()) {
            clearPendingRiskReview()
            return {
              status: 'failed',
              reason: 'risk-review-expired',
            }
          }
          return { status: 'risk-review-required' }
        }

        if (!hasValidSessionData(res.data)) {
          return {
            status: 'failed',
            reason: 'session-invalid',
          }
        }

        applyLoginSession(data.account, res.data)
        return { status: 'authenticated' }
      }
      catch (error: unknown) {
        console.error('登录失败', error)
        return {
          status: 'failed',
          reason: 'request-failed',
          message: getErrorMessage(error),
        }
      }
    }

    async function performRiskReviewCompletion(signal?: AbortSignal): Promise<RiskReviewCompletionResult> {
      const review = pendingRiskReview.value
      if (!review) {
        return { status: 'failed' }
      }
      if (Date.now() >= review.expiresAtTimestamp) {
        clearPendingRiskReview()
        return { status: 'expired' }
      }

      try {
        const res = await apiAuth.completeRiskReview(review.reviewToken, signal)
        const currentReview = pendingRiskReview.value

        if (!currentReview || currentReview.reviewToken !== review.reviewToken) {
          return { status: 'failed' }
        }
        if (res.status !== 1) {
          return {
            status: 'failed',
            message: res.error || res.message,
          }
        }
        if (!hasValidSessionData(res.data)) {
          return { status: 'failed' }
        }

        applyLoginSession(currentReview.account, res.data)
        clearPendingRiskReview()
        return { status: 'authenticated' }
      }
      catch (error: unknown) {
        if (signal?.aborted) {
          return { status: 'failed' }
        }
        console.error('完成登录风险审核失败', error)
        return {
          status: 'failed',
          message: getErrorMessage(error),
        }
      }
    }

    async function completeRiskReview(signal?: AbortSignal): Promise<RiskReviewCompletionResult> {
      if (riskReviewCompletionPromise) {
        return riskReviewCompletionPromise
      }

      const completionPromise = performRiskReviewCompletion(signal)
      riskReviewCompletionPromise = completionPromise
      try {
        return await completionPromise
      }
      finally {
        if (riskReviewCompletionPromise === completionPromise) {
          riskReviewCompletionPromise = null
        }
      }
    }

    // 手动登出
    function logout(redirect = router.currentRoute.value.fullPath) {
      window.dispatchEvent(new Event('echo:logout'))
      // 此处仅清除计算属性 isLogin 中判断登录状态过期的变量，以保证在弹出登录窗口模式下页面展示依旧正常
      localStorage.removeItem('token')
      localStorage.removeItem('user_id')
      localStorage.removeItem('tour')
      token.value = ''
      user_id.value = ''
      tour.value = false
      router.push({
        name: 'login',
        query: {
          ...(redirect !== settingsStore.settings.home.fullPath
            && router.currentRoute.value.name !== 'login' && { redirect }),
        },
      }).then(logoutCleanStatus)
    }
    // 请求登出
    async function requestLogout() {
      // 如果当前不是登录状态，则无需请求
      if (!isLogin.value) {
        console.warn('当前不是登录状态，无需请求登出')
        return
      }

      try {
        // 调用登出API，服务端会清除HttpOnly Cookie
        await apiAuth.logout()
      }
      catch (error) {
        console.error('登出请求失败', error)
      }

      window.dispatchEvent(new Event('echo:logout'))
      localStorage.removeItem('token')
      localStorage.removeItem('lastActivityTime')
      localStorage.removeItem('loginTime')
      localStorage.removeItem('autoLogoutTimeout') // 清除登出时间设置缓存
      localStorage.removeItem('tokenExpireTime') // 清除过期时间设置缓存
      localStorage.removeItem('tour')
      token.value = ''
      tour.value = false
      router.push({
        name: 'login',
        query: {
          ...(router.currentRoute.value.fullPath !== settingsStore.settings.home.fullPath
            && router.currentRoute.value.name !== 'login' && {
            redirect: router.currentRoute.value.fullPath,
          }),
        },
      }).then(logoutCleanStatus)
    }

    function logoutCleanStatus() {
      if (tokenRefreshTimer) {
        clearInterval(tokenRefreshTimer)
        tokenRefreshTimer = null
      }
      localStorage.removeItem('account')
      localStorage.removeItem('user_id')
      localStorage.removeItem('avatar')
      localStorage.removeItem('user_identifier')
      localStorage.removeItem('tour')
      account.value = ''
      user_id.value = ''
      avatar.value = ''
      email.value = ''
      user_identifier.value = ''
      tour.value = false
      permissions.value = []
      settingsStore.updateSettings({}, true)
      // tabbarStore.clean()
      routeStore.removeRoutes()
      menuStore.setActived(0)
    }

    // 获取权限
    async function getPermissions() {
      const res = await apiUser.permission()
      permissions.value = res.data.permissions
    }
    // 修改密码
    async function editPassword(data: {
      password: string
      newPassword: string
    }) {
      await apiUser.passwordEdit(data)
    }

    // 更新用户信息
    async function updateUserInfo(data: { avatar?: string, email?: string }) {
      try {
        // 更新成功后，更新本地存储和响应式状态
        if (data.avatar !== undefined) {
          localStorage.setItem('avatar', data.avatar)
          avatar.value = data.avatar
        }

        if (data.email !== undefined) {
          localStorage.setItem('email', data.email)
          email.value = data.email
        }
      }
      catch (error: any) {
        console.error('更新用户信息失败', error)
      }
    }

    // 页面刷新时恢复定时器
    // if (token.value && Number(localStorage.getItem('tokenExpireTime') ?? 0) > Date.now()) {
    //   startTokenRefreshTimer()
    // }

    // 获取是否为超级管理员
    async function checkAdmin() {
      try {
        const res = await apiUser.isAdmin()
        isAdmin.value = res.data.is_super_admin
        localStorage.setItem('isAdmin', JSON.stringify(res.data.is_super_admin))
      }
      catch (error: any) {
        console.error('获取是否为超级管理员失败', error)
      }
    }

    // 获取用户角色信息
    async function getRoleInfo() {
      try {
        const res = await apiUser.roleInfo()
        roleInfo.value = res.data
      }
      catch (error: any) {
        console.error('获取用户角色信息失败', error)
      }
    }

    function finishTour() {
      tour.value = false
      localStorage.removeItem('tour')
    }

    return {
      account,
      user_id,
      token,
      avatar,
      user_identifier,
      email,
      tour,
      pendingRiskReview,
      permissions,
      isLogin,
      isAdmin,
      isLicensee,
      roleInfo,
      isSuperAdmin,
      isGroupLeader,
      isDepartmentManager,
      isOperation,
      isSupplier,
      isStandaloneStationAssistant,
      isFinance,
      login,
      completeRiskReview,
      clearPendingRiskReview,
      logout,
      requestLogout,
      getPermissions,
      editPassword,
      updateUserInfo, // 添加更新用户信息的方法
      refreshToken, // 刷新token的方法
      getRoleInfo, // 获取用户角色信息的方法
      finishTour,
    }
  },
)
